import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Section from '../../../../components/section';
import Button from '../../../../components/button';
import AppInput from '../../../../components/form/base/app-input';
import { apiService } from '../../../../services/api.service';
import { errorService } from '../../../../services/error.service';
import { UserModel } from '../../../../models';
import './multi-merchant-users-page.scss';

const MultiMerchantUsersPage = () => {
    const navigate = useNavigate();

    const [merchants, setMerchants] = useState<UserModel[]>([]);
    const [merchantsLoading, setMerchantsLoading] = useState(false);

    // CREATE form state
    const [newLastName, setNewLastName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newSelected, setNewSelected] = useState<Set<string>>(new Set());
    const [creating, setCreating] = useState(false);

    // UPDATE form state
    const [editUserId, setEditUserId] = useState('');
    const [editSelected, setEditSelected] = useState<Set<string>>(new Set());
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        setMerchantsLoading(true);
        apiService.getAllMerchants()
            .then((response) => setMerchants(response.data ?? []))
            .catch((e) => {
                console.error('Failed to load merchants', e);
                errorService.showApiError(e);
            })
            .finally(() => setMerchantsLoading(false));
    }, []);

    const merchantsSorted = useMemo(() =>
        [...merchants].sort((a, b) =>
            (a.lastName ?? a.email ?? '').localeCompare(b.lastName ?? b.email ?? '')
        ),
        [merchants]
    );

    const toggle = (set: Set<string>, id: string, apply: (s: Set<string>) => void) => {
        const next = new Set(set);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        apply(next);
    };

    const newCanSubmit = !!newEmail && !!newPassword && !!newLastName && newSelected.size > 0 && !creating;
    const editCanSubmit = !!editUserId && editSelected.size > 0 && !updating;

    const onCreate = async () => {
        if (!newCanSubmit) return;
        setCreating(true);
        try {
            await apiService.createMultiMerchantUser(
                {
                    lastName: newLastName,
                    firstName: newLastName,
                    email: newEmail,
                    password: newPassword,
                },
                Array.from(newSelected),
            );
            errorService.showSuccess('Success', `Multi-merchant view user "${newLastName}" created.`);
            setNewLastName('');
            setNewEmail('');
            setNewPassword('');
            setNewSelected(new Set());
        } catch (e: any) {
            errorService.showApiError(e);
        } finally {
            setCreating(false);
        }
    };

    const onUpdate = async () => {
        if (!editCanSubmit) return;
        setUpdating(true);
        try {
            await apiService.updateAllowedMerchants(editUserId.trim(), Array.from(editSelected));
            errorService.showSuccess('Success', 'Allowed merchants updated.');
        } catch (e: any) {
            errorService.showApiError(e);
        } finally {
            setUpdating(false);
        }
    };

    const renderMerchantCheckboxes = (selected: Set<string>, apply: (s: Set<string>) => void) => {
        if (merchantsLoading) {
            return <span className='hint'>Loading merchants…</span>;
        }
        if (merchantsSorted.length === 0) {
            return <span className='hint'>No merchants available.</span>;
        }
        return (
            <div className='merchant-checkbox-list flex-column flex-gap-5'>
                {merchantsSorted.map((m) => {
                    if (!m.id) return null;
                    const id = m.id;
                    const label = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || m.email || id;
                    return (
                        <label key={id} className='flex-row flex-gap-10 flex-align-start-center'>
                            <input
                                type='checkbox'
                                checked={selected.has(id)}
                                onChange={() => toggle(selected, id, apply)}
                            />
                            <span>{label}</span>
                            <span className='secondary-text'>{m.email}</span>
                        </label>
                    );
                })}
            </div>
        );
    };

    return (
        <div className='multi-merchant-users-page page-content'>
            <div className='page-header flex-row flex-gap-20 flex-align-start-center'>
                <div className='page-title'>Multi-Merchant View Users</div>
                <Button label='Back' theme='secondary' onClick={() => navigate(-1)} />
            </div>

            <Section>
                <div className='flex-column flex-gap-20'>
                    <span className='label'>Create new multi-merchant view user</span>
                    <div className='flex-row flex-gap-20'>
                        <AppInput
                            type='text'
                            placeholder='Name'
                            value={newLastName}
                            onChange={(e) => setNewLastName(e.target.value)}
                        />
                        <AppInput
                            type='email'
                            placeholder='Email'
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />
                        <AppInput
                            type='password'
                            placeholder='Password'
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>
                    <span className='label'>Assigned merchants</span>
                    {renderMerchantCheckboxes(newSelected, setNewSelected)}
                    <div className='flex-row flex-align-end-end'>
                        <Button
                            label={creating ? 'Saving…' : 'Create user'}
                            disabled={!newCanSubmit}
                            onClick={onCreate}
                        />
                    </div>
                </div>
            </Section>

            <Section>
                <div className='flex-column flex-gap-20'>
                    <span className='label'>Update existing user's assigned merchants</span>
                    <AppInput
                        type='text'
                        placeholder='User ID'
                        value={editUserId}
                        onChange={(e) => setEditUserId(e.target.value)}
                    />
                    <span className='label'>Assigned merchants</span>
                    {renderMerchantCheckboxes(editSelected, setEditSelected)}
                    <div className='flex-row flex-align-end-end'>
                        <Button
                            label={updating ? 'Saving…' : 'Save assignments'}
                            disabled={!editCanSubmit}
                            onClick={onUpdate}
                        />
                    </div>
                </div>
            </Section>
        </div>
    );
};

export default MultiMerchantUsersPage;
